/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  AppBar as DefAppBar,
  Toolbar as DefToolbar,
  IconButton,
  Menu,
  MenuItem as DefMenuItem,
} from '@mui/material';
import { AppBarProps as DefAppBarProps } from '@mui/material/AppBar';
import { useServerErrorVar } from '../utils/apollo-vars';
import { useIsTablet, useIsDashboardPage } from '../hooks';
import { useUserIdQuery, useUserAdminStatusQuery } from '../apollo';
import { getColor } from '../styles';
import { useDrawerContext, useAppTitle, useExchange } from '../providers';
import logoImg from '../assets/images/logo-with-white-text.svg';
import { paths } from '../pages';
import { UserInCircleIcon as DefUserInCircleIcon } from './icons';
import { ExchangesMenu } from './portfolio';
import { Text } from './texts';

export type AppBarProps = DefAppBarProps;

const AppBar = (props: AppBarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isTablet = useIsTablet();
  const { title } = useAppTitle();
  const dashboardPage = useIsDashboardPage();
  const { exchange } = useExchange();
  const { mobileOpened, dispatch } = useDrawerContext();
  const { data: userIdData } = useUserIdQuery();
  const { data: userAdminData } = useUserAdminStatusQuery();
  const serverError = useServerErrorVar();
  const userId = userIdData?.getUser.id ?? 0;
  const isAdmin = userAdminData?.getUser.isAdmin ?? false;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    handleClose();
  }, [pathname, handleClose]);

  useEffect(() => {
    if (anchorEl) {
      document.addEventListener('wheel', handleClose);
      document.addEventListener('touchmove', handleClose);
    } else {
      document.removeEventListener('wheel', handleClose);
      document.removeEventListener('touchmove', handleClose);
    }

    return () => {
      document.removeEventListener('wheel', handleClose);
      document.removeEventListener('touchmove', handleClose);
    };
  }, [anchorEl, handleClose]);

  const withExchangesMenu = !serverError && dashboardPage && !!exchange;

  return (
    <DefAppBar color={'inherit'} {...props}>
      <Toolbar>
        {!isTablet ? (
          <>
            {withExchangesMenu ? (
              <>
                <div aria-hidden="true" />
                <ExchangesMenu />
              </>
            ) : (
              <PageTitle component={'h1'}>{title}</PageTitle>
            )}
            <AccountButton
              aria-controls="user-menu"
              aria-haspopup="true"
              onClick={handleClick}
              onMouseOver={handleClick}
            >
              <UserInCircleIcon />
            </AccountButton>
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              keepMounted
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              MenuListProps={{ onMouseLeave: handleClose }}
              onClose={handleClose}
            >
              <MenuItem disabled>ID: {userId}</MenuItem>
              {[
                {
                  title: 'HEADER__accountMenuItemText',
                  page: isAdmin ? paths.adminAccount : paths.account,
                  disabled: serverError,
                },
                {
                  title: 'HEADER__logoutMenuItemText',
                  page: paths.logout,
                },
              ].map(({ title, page, ...props }) => (
                <MenuItem key={title} onClick={() => navigate(page)} {...props}>
                  {t(title)}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <>
            <LogoImg src={logoImg} alt={'Logo'} />
            <MobileMenuButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => dispatch({ type: 'toggleMobile' })}
            >
              {mobileOpened ? (
                <CloseIcon style={{ color: '#fff' }} />
              ) : (
                <MenuIcon style={{ color: '#fff' }} />
              )}
            </MobileMenuButton>
          </>
        )}
      </Toolbar>
    </DefAppBar>
  );
};

const Toolbar = styled(DefToolbar)`
  justify-content: space-between;
  background-color: ${getColor('blue')};

  ${({ theme }) => theme.breakpoints.down('md')} {
    background-color: ${getColor('darkBlue')};
  }
`;

const MobileMenuButton = styled(IconButton)`
  margin-right: -1px;
  position: relative;
  padding: 7px;

  /*noinspection CssUnusedSymbol*/
  .MuiSvgIcon-root {
    font-size: 32px;
  }
`;

const PageTitle = styled(Text)`
  font-size: 1rem;
  color: #fff;
`;

const LogoImg = styled.img`
  width: 230px;
  margin-left: 5px;
`;

const UserInCircleIcon = styled(DefUserInCircleIcon)`
  width: 24px;
  height: 24px;
`;

const AccountButton = styled(IconButton)`
  margin-right: -8px;
`;

// noinspection CssUnusedSymbol
const MenuItem = styled(DefMenuItem)`
  background-color: transparent;
  &.Mui-focusVisible {
    background-color: transparent;
  }
  &:disabled,
  &.Mui-disabled {
    color: ${getColor('darkBlue')};
    opacity: 1;
  }
`;

export { AppBar };
